import { Koi } from '@ksyos/koi';
import type { ISchemaContainer } from './schemaContainer';
import { SpirometerSoftwareNames } from '../enums/spirometerSoftwareNames';
import { phoneNumberSchema } from './schemas/phoneNumberSchema';

export interface IAutoSubsequentServiceValidated {
    isDeviation: boolean;
    providerOrganizationId: string;
    providerPractitionerId: string | null;
    subsequentServiceId: string;
}

const autoSubsequentService = Koi.object({
    isDeviation: Koi.boolean(),
    providerOrganizationId: Koi.string().uuid(),
    providerPractitionerId: Koi.string().uuid().allow(null),
    subsequentServiceId: Koi.string().uuid(),
});

export interface IServiceConsumerZorgMailPreferencesValidated {
    sendZorgMailOnExaminationRequest: boolean;
    sendZorgMailOnReferralIntake: boolean;
    sendZorgMailOnReferralRequest: boolean;
}

const serviceConsumerZorgMailPreferences = Koi.object().keys({
    sendZorgMailOnExaminationRequest: Koi.boolean(),
    sendZorgMailOnReferralIntake: Koi.boolean(),
    sendZorgMailOnReferralRequest: Koi.boolean(),
});

export const serviceConsumerZorgMailPreferencesSchema: ISchemaContainer<IServiceConsumerZorgMailPreferencesValidated> =
    {
        schema: serviceConsumerZorgMailPreferences.options({ convert: false, presence: 'required' }),
    };

export interface IServiceConsumerContactDetailsValidated {
    phoneNumberConsult: string | null;
}

const serviceConsumerContactDetails = Koi.object().keys({
    phoneNumberConsult: phoneNumberSchema.allow(null),
});

export const serviceConsumerContactDetailsSchema: ISchemaContainer<IServiceConsumerContactDetailsValidated> = {
    schema: serviceConsumerContactDetails.options({ convert: false, presence: 'required' }),
};

export interface IServiceConsumerEmailNotificationPreferencesValidated {
    sendNotificationEmployment: boolean;
    sendNotificationEmploymentAlt: boolean;
    sendNotificationPractitioner: boolean;
    sendNotificationPractitionerWhenDeviant: boolean;
}

const serviceConsumerEmailNotificationPreferences = Koi.object().keys({
    sendNotificationEmployment: Koi.boolean(),
    sendNotificationEmploymentAlt: Koi.boolean(),
    sendNotificationPractitioner: Koi.boolean(),
    sendNotificationPractitionerWhenDeviant: Koi.boolean(),
});

// eslint-disable-next-line @stylistic/max-len
export const serviceConsumerEmailNotificationPreferencesSchema: ISchemaContainer<IServiceConsumerEmailNotificationPreferencesValidated> =
    {
        schema: serviceConsumerEmailNotificationPreferences.options({ convert: false, presence: 'required' }),
    };

export interface IServiceConsumerGgzPreferencesValidated {
    followUpMeasurementGgz: boolean;
    weeksForFollowUpMeasurementGgz: number | null;
}

const serviceConsumerGgzPreferences = Koi.object({
    followUpMeasurementGgz: Koi.boolean(),
    weeksForFollowUpMeasurementGgz: Koi.when('followUpMeasurementGgz', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.number().min(1).max(999).allow(null),
    }),
});

interface IServiceConsumerBasePreferencesValidated {
    autoSubsequentService: IAutoSubsequentServiceValidated | null;
    followUpMeasurementGgz: boolean;
    isAutoCloseRequest: boolean;
    isAutoCreated?: boolean;
    isGraderSelector: boolean;
    isRequestCreator: boolean;
    spirometerSoftwareName: SpirometerSoftwareNames | null;
    startDate: string;
    usingRestEcgMeter: boolean;
    usingSpirometer: boolean;
}

export const serviceConsumerBasePreferences = Koi.object().keys({
    autoSubsequentService: autoSubsequentService.allow(null).optional(),
    followUpMeasurementGgz: Koi.boolean(),
    isAutoCloseRequest: Koi.boolean(),
    isAutoCreated: Koi.boolean().optional(),
    isGraderSelector: Koi.boolean(),
    isRequestCreator: Koi.boolean(),
    spirometerSoftwareName: Koi.koi()
        .enum(SpirometerSoftwareNames)
        .when('usingSpirometer', { is: true, otherwise: Koi.valid(null), then: Koi.disallow(null) }),
    startDate: Koi.koi().date(),
    usingRestEcgMeter: Koi.boolean(),
    usingSpirometer: Koi.boolean(),
});

interface IServiceConsumerEditValidated
    extends IServiceConsumerContactDetailsValidated,
        IServiceConsumerZorgMailPreferencesValidated,
        IServiceConsumerEmailNotificationPreferencesValidated,
        IServiceConsumerBasePreferencesValidated {
    compensationPayoutRequest: number | null;
    endDate: string | null;
    sendNotificationEmploymentAlt: boolean;
}

export const serviceConsumerEdit = serviceConsumerContactDetails
    .concat(serviceConsumerZorgMailPreferences)
    .concat(serviceConsumerEmailNotificationPreferences)
    .concat(serviceConsumerBasePreferences)
    .keys({
        compensationPayoutRequest: Koi.number().min(0).precision(0).allow(null),
        endDate: Koi.koi().date().endDate().allow(null),
        followUpMeasurementGgz: Koi.boolean(),
        sendNotificationEmploymentAlt: Koi.boolean(),
        spirometerSoftwareName: Koi.koi()
            .enum(SpirometerSoftwareNames)
            .when('usingSpirometer', { is: true, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
        usingRestEcgMeter: Koi.boolean(),
        usingSpirometer: Koi.boolean(),
    });

export const serviceConsumerEditSchema: ISchemaContainer<IServiceConsumerEditValidated> = {
    schema: serviceConsumerEdit.options({ convert: false, presence: 'required' }),
};

export const serviceConsumerGgzPreferencesSchema: ISchemaContainer<IServiceConsumerGgzPreferencesValidated> = {
    schema: serviceConsumerGgzPreferences.options({ convert: false, presence: 'required' }),
};

interface IServiceConsumerPersonalSettingsEditValidated
    extends IServiceConsumerBasePreferencesValidated,
        IServiceConsumerGgzPreferencesValidated {}

export const serviceConsumerPersonalSettingsEdit = serviceConsumerBasePreferences.concat(serviceConsumerGgzPreferences);

// eslint-disable-next-line @stylistic/max-len
export const serviceConsumerPersonalSettingsEditSchema: ISchemaContainer<IServiceConsumerPersonalSettingsEditValidated> =
    {
        schema: serviceConsumerPersonalSettingsEdit.options({ convert: false, presence: 'required' }),
    };

interface IDeleteServiceConsumerValidated {
    endDateIsCurrentDate: boolean;
}

const deleteServiceConsumer = Koi.object({
    endDateIsCurrentDate: Koi.boolean(),
});

export type IServiceConsumerUpdateValidated =
    | IDeleteServiceConsumerValidated
    | IServiceConsumerEditValidated
    | IServiceConsumerPersonalSettingsEditValidated;

const serviceConsumerUpdate = Koi.alternatives().try(
    serviceConsumerEdit,
    serviceConsumerPersonalSettingsEdit,
    deleteServiceConsumer
);

export const serviceConsumerUpdateSchema: ISchemaContainer<IServiceConsumerUpdateValidated> = {
    schema: serviceConsumerUpdate.options({ convert: false, presence: 'required' }),
};

export interface IServiceConsumerAddValidated extends IServiceConsumerEditValidated {
    employmentId: string;
    serviceId: string;
}

export const serviceConsumerAddSchema: ISchemaContainer<IServiceConsumerAddValidated> = {
    schema: serviceConsumerEdit
        .concat(
            Koi.object({
                employmentId: Koi.string().uuid(),
                serviceId: Koi.string().uuid(),
            })
        )
        .options({ convert: false, presence: 'required' }),
};

export const regexForTxcsIdCheck = /^\d+$/;

export function isServiceConsumerEditValidated(
    schemaInterface: IServiceConsumerUpdateValidated | IServiceConsumerPersonalSettingsEditValidated
): schemaInterface is IServiceConsumerEditValidated | IServiceConsumerPersonalSettingsEditValidated {
    return (
        (schemaInterface as IServiceConsumerEditValidated | IServiceConsumerPersonalSettingsEditValidated).startDate !==
            undefined &&
        (schemaInterface as IServiceConsumerEditValidated | IServiceConsumerPersonalSettingsEditValidated)
            .autoSubsequentService !== undefined
    );
}

export function isDeleteServiceConsumerValidated(
    schemaInterface: IServiceConsumerUpdateValidated
): schemaInterface is IDeleteServiceConsumerValidated {
    return (schemaInterface as IDeleteServiceConsumerValidated).endDateIsCurrentDate;
}
